import type {
  DimensionBridgeSnapshot,
  DimensionalEvent,
  DimensionalMirror,
  MirrorMemoryEntry,
} from "./dimensional-bridge";

export type DimensionalEvolutionKind =
  | "memory-echo"
  | "blue-avatar-encounter"
  | "stability-recovery"
  | "branch-scout"
  | "mirror-ecology";

export interface DimensionalEvolutionProposal {
  id: string;
  kind: DimensionalEvolutionKind;
  mirrorId: string;
  atMs: number;
  priority01: number;
  reason: string;
  evidence: string[];
  parameters: Record<string, string | number | boolean>;
  status: "proposed";
}

/**
 * Bounded idea-evolution layer for the dimensional network.
 *
 * The director can only produce simulation proposals from dimensional evidence.
 * It cannot write files, access devices, call RealityBridge, or mutate a mirror.
 * Application remains an explicit, separately governed step through Nexus/AKH.
 */
export class DimensionalEvolutionDirector {
  private readonly proposals = new Map<string, DimensionalEvolutionProposal>();
  private serial = 0;

  observe(event: DimensionalEvent, snapshot: DimensionBridgeSnapshot): DimensionalEvolutionProposal[] {
    const mirror = snapshot.mirrors.find(item => item.id === event.mirrorId);
    if (!mirror) return [];
    const history = snapshot.mirrorMemory.filter(item => item.mirrorId === mirror.id);
    const generated: DimensionalEvolutionProposal[] = [];

    if (event.type === "portal-opened") {
      generated.push(this.propose(
        "blue-avatar-encounter",
        mirror,
        event.atMs,
        0.78,
        "A stable aperture can support a bounded fifth-dimensional manifestation encounter.",
        ["portal-opened", `stability:${mirror.stability01.toFixed(3)}`, `energy:${mirror.energy01.toFixed(3)}`],
        {
          avatarRole: history.length > 2 ? "recognizer" : "observer",
          manifestationColor: "spectral-blue",
          crossingAllowed: mirror.stability01 >= 0.72,
        },
      ));
    }

    if (event.type === "crossing-complete") {
      const last = history[history.length - 1];
      generated.push(this.memoryEcho(mirror, event.atMs, last, history.length));
      if (history.length > 0 && history.length % 3 === 0) {
        generated.push(this.propose(
          "mirror-ecology",
          mirror,
          event.atMs,
          0.62,
          "Repeated crossings justify local environmental response without changing portal authority.",
          ["crossing-complete", `history-count:${history.length}`],
          {
            radiusM: Math.min(18, 4 + history.length * 1.5),
            bioluminescence01: Math.min(0.9, 0.22 + history.length * 0.08),
            wildlifeAvoidance01: Math.min(0.75, 0.12 + history.length * 0.05),
          },
        ));
      }
      if (history.length >= 5) {
        generated.push(this.propose(
          "branch-scout",
          mirror,
          event.atMs,
          0.58,
          "Sufficient mirror memory exists to propose a non-destructive alternate-reality observation branch.",
          ["crossing-complete", `history-count:${history.length}`],
          {
            observationOnly: true,
            branchDepth: Math.min(3, Math.floor(history.length / 5)),
            requiresNewMirrorEndpoint: true,
          },
        ));
      }
    }

    if (event.type === "portal-collapsed" && mirror.energy01 < 0.25) {
      generated.push(this.propose(
        "stability-recovery",
        mirror,
        event.atMs,
        0.86,
        "Portal collapse with low energy should recover before another crossing.",
        ["portal-collapsed", `energy:${mirror.energy01.toFixed(3)}`],
        {
          recoveryWindowMs: 45000,
          targetEnergy01: 0.55,
          crossingsLocked: true,
        },
      ));
    }

    for (const proposal of generated) this.proposals.set(proposal.id, proposal);
    this.trim();
    return generated.map(item => ({ ...item, evidence: [...item.evidence], parameters: { ...item.parameters } }));
  }

  recent(limit = 16): DimensionalEvolutionProposal[] {
    return [...this.proposals.values()]
      .sort((a, b) => b.atMs - a.atMs || b.priority01 - a.priority01 || a.id.localeCompare(b.id))
      .slice(0, Math.max(0, Math.trunc(limit)))
      .map(item => ({ ...item, evidence: [...item.evidence], parameters: { ...item.parameters } }));
  }

  clear(): void {
    this.proposals.clear();
    this.serial = 0;
  }

  private memoryEcho(
    mirror: DimensionalMirror,
    atMs: number,
    last: MirrorMemoryEntry | undefined,
    historyCount: number,
  ): DimensionalEvolutionProposal {
    return this.propose(
      "memory-echo",
      mirror,
      atMs,
      0.7,
      "Every completed crossing leaves a bounded mirror-memory echo that presentation and narrative systems may consume.",
      ["crossing-complete", `history-count:${historyCount}`, last ? `memory:${last.id}` : "memory:pending"],
      {
        echoDurationMs: Math.min(18000, 5000 + historyCount * 900),
        visualStrength01: Math.min(0.92, 0.35 + historyCount * 0.06),
        audible: historyCount >= 2,
      },
    );
  }

  private propose(
    kind: DimensionalEvolutionKind,
    mirror: DimensionalMirror,
    atMs: number,
    priority01: number,
    reason: string,
    evidence: string[],
    parameters: Record<string, string | number | boolean>,
  ): DimensionalEvolutionProposal {
    return {
      id: `dimension-evolution:${mirror.id}:${kind}:${atMs}:${++this.serial}`,
      kind,
      mirrorId: mirror.id,
      atMs,
      priority01: Math.max(0, Math.min(1, priority01)),
      reason,
      evidence,
      parameters,
      status: "proposed",
    };
  }

  private trim(): void {
    const ordered = [...this.proposals.values()]
      .sort((a, b) => b.atMs - a.atMs || b.priority01 - a.priority01 || a.id.localeCompare(b.id));
    if (ordered.length <= 128) return;
    const keep = new Set(ordered.slice(0, 128).map(item => item.id));
    for (const id of this.proposals.keys()) if (!keep.has(id)) this.proposals.delete(id);
  }
}
